import './i18n';

import { FC, lazy, Suspense, useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import useNavigatorOnline from 'use-navigator-online';

import { GlobalLoader, ProtectedRoute, ServiceModal } from '@components';
import { ConfigurationLayout, LoginLayout, MainContentContainer, MainLayout, WorkspaceLayout } from '@containers';
import { useAppDispatch, useClearInterval, useRoutePermissions, useTokenManager } from '@hooks';
import { appInit, runSaga, systemUpdateNetworkConnection, useCurrentWorkspaceId, useInitLoading } from '@store';

import { Cache } from '@pages/Cdn/subPages/Cache/Cache';
import { Cdn } from '@pages/Cdn/subPages/Cdn/Cdn';
import { CdnDashboard } from '@pages/Cdn/subPages/CdnDashboard/CdnDashboard';
import { AppConfiguration } from '@pages/Cdn/subPages/Configuration/AppConfiguration';
import { Domain } from '@pages/Cdn/subPages/Domain/Domain';
import { Origins } from '@pages/Cdn/subPages/Origins/Origins';
import { PageRules } from '@pages/Cdn/subPages/PageRules/PageRules';
import { Ssl } from '@pages/Cdn/subPages/SSL/SSL';
import { Security } from '@pages/Cdn/subPages/Security/Security';
import { Waf } from '@pages/Cdn/subPages/WAF/WAF';
import { CustomWAFRules } from '@pages/Cdn/subPages/WAF/subComponents/CustomWAFModal';
import { HomePage } from '@pages/HomePage/HomePage';
import { Login } from '@pages/Login/Login';
import {
  CreateNewPassword,
  CreateUserAccount,
  RecoverPassword,
  VerifyEmail,
  WorkspaceInvitation,
} from '@pages/Login/subPages';
import { ReverifyEmail } from '@pages/Login/subPages/reverifyEmail/reverifyEmail';
import { Maintenance } from '@pages/Maintenance/Maintenance';
import { ProfileSettings } from '@pages/Profile/ProfileSettings';
import { WorkspaceConfiguration } from '@pages/Workspace';
import { Workspace } from '@pages/Workspace/workspace';

import { ROUTES } from '@constants';
import rootSaga from '@sagas';

const Snacks = lazy(() => import('./components/Snacks/Snacks'));

export const App: FC = () => {
  const dispatch = useAppDispatch();
  const dispatchRef = useRef(dispatch);
  const hasWorkspace = !!useCurrentWorkspaceId();

  useEffect(() => {
    dispatchRef.current = dispatch;
  }, [dispatch]);

  useEffect(() => {
    runSaga(rootSaga);
    dispatchRef.current(appInit());
  }, []);

  useTokenManager();
  useClearInterval();

  const { showRoute } = useRoutePermissions();
  const isInitLoading: boolean = useInitLoading();
  const { isOnline, isOffline, backOnline, backOffline } = useNavigatorOnline();

  useEffect(() => {
    if (backOnline || backOffline) {
      dispatch({
        type: systemUpdateNetworkConnection.type,
        payload: {
          isOnline,
          isOffline,
          backOnline,
          backOffline,
        },
      });
    }
  }, [backOnline, backOffline, isOnline, isOffline]);

  if (isInitLoading) return <GlobalLoader overlay />;
  return (
    <Router>
      <DndProvider backend={HTML5Backend}>
        <Suspense fallback={<GlobalLoader overlay />}>
          <div className="relative h-full w-full overflow-hidden">
            <Snacks />
            <ServiceModal />
            <Routes>
              <Route
                path={ROUTES.login}
                element={
                  <ProtectedRoute isAllowed={showRoute.login}>
                    <LoginLayout>
                      <Login />
                    </LoginLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.registration}
                element={
                  <ProtectedRoute isAllowed={showRoute.login}>
                    <LoginLayout>
                      <CreateUserAccount />
                    </LoginLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.resetNewPassword}
                element={
                  <ProtectedRoute isAllowed={showRoute.login}>
                    <LoginLayout>
                      <CreateNewPassword />
                    </LoginLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.reverifyEmail}
                element={
                  <ProtectedRoute isAllowed={showRoute.login}>
                    <LoginLayout>
                      <ReverifyEmail />
                    </LoginLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.verifyEmail}
                element={
                  <ProtectedRoute isAllowed={showRoute.login}>
                    <LoginLayout>
                      <VerifyEmail />
                    </LoginLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.recoverPassword}
                element={
                  <ProtectedRoute isAllowed={showRoute.login}>
                    <LoginLayout>
                      <RecoverPassword />
                    </LoginLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path={ROUTES.workspace.invite}
                element={
                  <ProtectedRoute isAllowed>
                    <WorkspaceInvitation />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.maintenance}
                element={
                  <ProtectedRoute isAllowed>
                    <Maintenance />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.homepage}
                element={
                  <ProtectedRoute isAllowed={showRoute.homepage}>
                    <MainContentContainer>
                      <MainLayout>
                        <HomePage />
                      </MainLayout>
                    </MainContentContainer>
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.profileSettings}
                element={
                  <ProtectedRoute isAllowed={showRoute.profileSettings}>
                    <MainContentContainer>
                      <MainLayout>
                        <ProfileSettings />
                      </MainLayout>
                    </MainContentContainer>
                  </ProtectedRoute>
                }
              />
              <Route path={ROUTES.workspace.main}>
                <Route
                  index
                  element={
                    <ProtectedRoute isAllowed={showRoute.workspace && hasWorkspace}>
                      <MainContentContainer>
                        <MainLayout>
                          <WorkspaceLayout>
                            <Workspace />
                          </WorkspaceLayout>
                        </MainLayout>
                      </MainContentContainer>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path={`:id${ROUTES.workspace.settings}/*`}
                  element={
                    <ProtectedRoute isAllowed={showRoute.workspace && hasWorkspace}>
                      <MainContentContainer>
                        <MainLayout>
                          <WorkspaceLayout>
                            <WorkspaceConfiguration />
                          </WorkspaceLayout>
                        </MainLayout>
                      </MainContentContainer>
                    </ProtectedRoute>
                  }
                />
                <Route path=":id" element={<Navigate to="settings" replace />} />
              </Route>
              <Route path={ROUTES.cdn}>
                <Route
                  index
                  element={
                    <ProtectedRoute isAllowed={showRoute.cdn && hasWorkspace}>
                      <MainContentContainer>
                        <MainLayout>
                          <Cdn />
                        </MainLayout>
                      </MainContentContainer>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`:id/${ROUTES.configuration.main}/*`}
                  element={
                    <ProtectedRoute isAllowed={showRoute.cdn && hasWorkspace}>
                      <MainContentContainer>
                        <ConfigurationLayout>
                          <MainLayout>
                            <Routes>
                              <Route index element={<Navigate to={ROUTES.configuration.dashboard} replace />} />
                              <Route path={ROUTES.configuration.domain} element={<Domain />} />
                              <Route path={ROUTES.configuration.origins} element={<Origins />} />
                              <Route path={ROUTES.configuration.ssl} element={<Ssl />} />
                              <Route path={ROUTES.configuration.cache} element={<Cache />} />
                              <Route path={ROUTES.configuration.dashboard} element={<CdnDashboard />} />
                              <Route path={ROUTES.configuration.pagerules} element={<PageRules />} />
                              <Route path={ROUTES.configuration.waf.main} element={<Waf />} />
                              <Route path={ROUTES.configuration.security} element={<Security />} />
                              <Route
                                path={`${ROUTES.configuration.waf.main}/${ROUTES.configuration.waf.customRules}`}
                                element={<CustomWAFRules />}
                              />
                              <Route path={ROUTES.configuration.appConfiguration} element={<AppConfiguration />} />
                            </Routes>
                          </MainLayout>
                        </ConfigurationLayout>
                      </MainContentContainer>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id"
                  element={<Navigate to={`${ROUTES.configuration.main}/${ROUTES.configuration.dashboard}`} replace />}
                />
              </Route>
              <Route path="*" element={<Navigate to={ROUTES.homepage} replace />} />
            </Routes>
          </div>
        </Suspense>
      </DndProvider>
    </Router>
  );
};
